import { PublicClientApplication, LogLevel } from "@azure/msal-browser";
import { purgeLegacyInteractionLock } from "../utils/msalInteractionLock";

/**
 * MSAL Configuration for Azure AD / Entra ID Authentication
 */
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MSAL_TENANT}`,
    // MSAL v5 routes every authentication response through the redirect bridge
    // page rather than back into the app (see /redirect.html). Entra has COOP
    // on by default, which severs a popup's or hidden iframe's channel to its
    // opener, so the bridge receives the response same-origin and rebroadcasts
    // it over a BroadcastChannel.
    //
    // This exact string must also be registered as a SPA redirect URI on the
    // Entra app registration — including the path — or every sign-in fails with
    // redirect_uri_mismatch. Deploy previews are registered automatically by
    // netlify/plugins/azure-redirect.
    redirectUri: `${window.location.origin}/redirect.html`,
    // Deliberately the bare origin, not the bridge: the bridge is only required
    // on postLogoutRedirectUri for logoutPopup(), and we only ever call
    // logoutRedirect() (Sidebar.jsx). Pointing this at the bridge would land
    // signed-out users on a blank "Signing in" page instead of the app root.
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
    // Governs only entries written by pre-v5 MSAL, during the one-time cache
    // migration from schema 2 to 3 (v5 raised both ACCOUNT_SCHEMA_VERSION and
    // CREDENTIAL_SCHEMA_VERSION). Anything older than this window is discarded
    // on first load of v5 instead of being carried forward, which costs that
    // user a re-login. The 5-day default would have signed out anyone away for
    // a week; 90 covers a quarter. Current-schema entries are never subject to
    // it, so this becomes inert once the fleet has migrated.
    cacheRetentionDays: 90,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          default:
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
    // Give the hidden-iframe silent renewal more room before it throws
    // `timed_out`. This was `iframeHashTimeout` under v4; v5 renamed it because
    // the mechanism changed — it no longer polls the iframe's location hash, it
    // waits for the redirect bridge to broadcast on a BroadcastChannel. The
    // name change is silent: v5 ignores the old key, so leaving it would have
    // quietly reverted us to the 10s default. 12s lets legitimate renewals
    // complete on slow networks and slow-responding tenants. When the iframe
    // genuinely can't finish (blocked third-party cookies, guest/B2B accounts),
    // acquireToken's redirect fallback takes over — see
    // src/utils/msalTokenManager.js.
    iframeBridgeTimeout: 12000,
  },
};

export const loginRequest = {
  scopes: ["User.Read"],
};

export const graphCalendarRequest = {
  scopes: ["Calendars.ReadWrite"],
};

export const graphMailRequest = {
  scopes: ["Mail.Send"],
};

export const graphMailReadRequest = {
  scopes: ["Mail.ReadWrite"],
};

// Replying INTO an existing thread needs both halves: Mail.ReadWrite to find
// the original message in the user's own mailbox and draft the reply against
// it, Mail.Send to send it. Used by the truck-order flow, which answers the
// purchase-order thread the supplier already has rather than starting a new one.
export const graphMailReplyRequest = {
  scopes: ["Mail.ReadWrite", "Mail.Send"],
};

/**
 * Stores any error from the MSAL redirect flow so components can display it.
 * This is set once during initialization (before React renders).
 */
let _msalRedirectError = null;
export function getMsalRedirectError() { return _msalRedirectError; }

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

// Accept only same-origin in-app paths. Rejects protocol-relative URLs (`//x`),
// backslash-injection (`/\x`), and absolute URLs with a scheme — all of which
// can be abused to redirect the user off-site after login.
function isSafeRedirectPath(path) {
  return typeof path === 'string'
    && /^\/[A-Za-z0-9_\-\/?&=.]*$/.test(path)
    && !path.startsWith('//')
    && !path.includes('\\');
}

/**
 * Returns and clears the saved post-login redirect path (if any).
 * Called once after a successful login redirect to restore the user's
 * original location.
 */
let _postLoginRedirect = null;
export function consumePostLoginRedirect() {
  const path = _postLoginRedirect;
  _postLoginRedirect = null;
  return path;
}

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL instance
await msalInstance.initialize();

// Drop MSAL temporary-cache orphans left in localStorage by older MSAL versions.
// Under v5 this is hygiene rather than a fix: v5 pins the temporary cache to
// sessionStorage and dropped v4's localStorage read-fallback, so an orphan there
// is inert. It is still worth clearing — the orphans are real, and they are what
// wedged interactive requests with `interaction_in_progress` under v4. Must run
// before handleRedirectPromise(): a real in-flight redirect keeps its state in
// sessionStorage, so nothing removed here can belong to a live request.
const purgedLockKeys = purgeLegacyInteractionLock();
if (purgedLockKeys.length > 0) {
  console.warn("MSAL: cleared stale temporary cache keys", purgedLockKeys);
}

// Handle redirect response after login
try {
  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
    // The user just completed a login redirect — read the saved URL
    const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    _postLoginRedirect = isSafeRedirectPath(saved) ? saved : null;
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } else {
    // Set active account if one exists in cache
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
} catch (error) {
  console.error("MSAL redirect error:", error);
  _msalRedirectError = error;
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
}
